import { getPortalHost } from "@/utils/portalHost";

export const ROUTES = {
  home: "/",
  faq: "/faq",
  privacy: "/privacy",
  terms: "/terms",
  about: "/about",
  aboutUs: "/about-us",
  contact: "/contact",
  specialties: "/specialties",
  insurance: "/insurance",
  careers: "/careers",
  homeRemedies: "/home-remedies",
  features: "/features",
  providersGrow: "/providers/grow",

  oauthCallback: "/oauth/callback",
  verifyEmail: "/auth/verify-email",
  magicLink: "/auth/magic-link",
  resetPassword: "/auth/reset-password",

  //search Routes
  results: "/search/results",
  providerDetail: "/search/provider/:id",
  rxPriceCompare: "/rx-price-compare",
  patientSupport: "/patient-support",
  patientSupportCareGuide: "/patient-support/care-guide/:id",
  microEntrySpeciality: "/care/speciality/:specialityName",
  microEntrySpecialityWithZip: "/care/speciality/:specialityName/:zipcode",
  microEntryTaxonomy: "/care/taxonomy/:taxonomyCodeOrName",
  microEntryTaxonomyWithZip: "/care/taxonomy/:taxonomyCodeOrName/:zipcode",
  microEntryConversation: "/care/conversation/:symptomSlug",
  microEntryConversationWithZip: "/care/conversation/:symptomSlug/:zipcode",
  microEntryHomeRemedy: "/care/home-remedy/:symptomSlug",
  vaccineScheduler: "/vaccine-scheduler",

  //user Routes
  login: "/user/login",
  register: "/user/signup",
  forgotPassword: "/user/forgot-password",
  profile: "/user/profile",
  userTracker: "/user/tracker",
  userTrackerInsights: "/user/tracker/insights",
  myRecentSearchHistories: "/user/my-recent-search-histories",
  savedProviders: "/user/my-saved-providers",
  appointments: "/user/my-appointments",
  familyMembers: "/user/family-members",
  subscriptions: "/user/subscriptions",
  userBilling: "/user/billing",
  subscriptionsSuccess: "/user/subscriptions/success",
  subscriptionsCancel: "/user/subscriptions/cancel",
  bookingRequests: "/user/my-booking-requests",
  bookingRequestDetail: "/user/my-booking-requests/:id",
  messages: "/user/messages",
  messageThreadDetail: "/user/messages/:id",

  // provider portal routes
  providersHome: "/providers",
  providersLogin: "/providers/login",
  providersForgotPassword: "/providers/forgot-password",
  providersResetPassword: "/providers/reset-password",
  providersChangePassword: "/providers/change-password",
  providersAppointments: "/providers/appointments",
  providersLeads: "/providers/leads",
  providersBilling: "/providers/billing",
  providersSubscriptions: "/providers/subscriptions",
  providersProfile: "/providers/profile",
  providersSettings: "/providers/settings",
  providersManagement: "/providers/management",
  providersManageSpeciality: "/providers/settings/manage-speciality",
  providersManageInsurance: "/providers/settings/manage-insurance",
  providersLeadScoring: "/providers/settings/lead-scoring",
  providersSupport: "/providers/support",
  providersStaff: "/providers/staff",
  providersSchedule: "/providers/schedule",
  providersAnalytics: "/providers/analytics/search-intelligence",
  providersMessages: "/providers/messages",
  providersMessageThreadDetail: "/providers/messages/:id",
  providerPatientDetail: "/providers/patients/:id",
  providerLeadDetail: "/providers/leads/:id",

  // admin portal routes
  adminsHome: "/admins",
  adminsLogin: "/admins/login",
  adminsAnalytics: "/admins/analytics/search-intelligence",
  adminsEmailTracking: "/admins/analytics/email-tracking",
  adminsBillingReports: "/admins/billing/reports",
  // adminsBillingOverview: "/admins/billing/overview",
  adminsChargeRules: "/admins/billing/charge-rules",
  adminsSubscriptions: "/admins/subscriptions",
  adminsLeadsReports: "/admins/leads/reports",
  adminsServiceIntegrations: "/admins/settings/service-integrations",
  adminsEmailTemplates: "/admins/settings/email-templates",
  adminsLeadScoring: "/admins/settings/lead-scoring",
  adminsLeadScoringTester: "/admins/settings/lead-scoring/tester",
  adminsCacheManagement: "/admins/settings/cache-management",
  adminsMfaConfiguration: "/admins/settings/mfa-configuration",
  
  adminsCommunicationCheck: "/admins/settings/communication-check",
  adminsProviderCommunications: "/admins/provider-communications",
  adminsSupport: "/admins/support",
  adminsProviderOnboarding: "/admins/providers/onboarding",
  adminsInsuranceVerification: "/admins/insurance-verification",
  adminsProviderGrowth: "/admins/providers/growth",
  adminsProviderAccess: "/admins/providers/access",
  adminsProviderDemoRequests: "/admins/providers/demo-requests",
  adminsProviderSignups: "/admins/providers/signups",
  adminsTimerJobs: "/admins/timer-jobs",
  adminsTimerJobDetail: "/admins/timer-jobs/:jobKey",
} as const;

export function getProviderDetailRoute(providerId: string) {
  return ROUTES.providerDetail.replace(":id", providerId);
}

export function getSpecialtyRoute(specialtyName: string, zipCode?: string) {
  const encodedSpecialty = encodeURIComponent(specialtyName);
  if (zipCode?.trim()) {
    return ROUTES.microEntrySpecialityWithZip
      .replace(":specialityName", encodedSpecialty)
      .replace(":zipcode", encodeURIComponent(zipCode.trim()));
  }
  return ROUTES.microEntrySpeciality.replace(":specialityName", encodedSpecialty);
}

export function getHomeRemedyRoute(symptomSlug: string) {
  return ROUTES.microEntryHomeRemedy.replace(
    ":symptomSlug",
    encodeURIComponent(symptomSlug),
  );
}

export function getBookingRequestDetailRoute(leadId: string) {
  return ROUTES.bookingRequestDetail.replace(":id", leadId);
}

export function getProviderPatientDetailRoute(patientId: string) {
  return ROUTES.providerPatientDetail.replace(":id", patientId);
}

export function getProviderLeadDetailRoute(leadId: string) {
  return ROUTES.providerLeadDetail.replace(":id", leadId);
}

export function getMessageThreadDetailRoute(threadId: string) {
  return ROUTES.messageThreadDetail.replace(":id", threadId);
}

export function getProviderMessageThreadDetailRoute(threadId: string) {
  return ROUTES.providersMessageThreadDetail.replace(":id", threadId);
}

export function getLoginRouteForRole(role?: string) {
  const portal = getPortalHost();
  if (role === "admin") return portal === "admin" ? "/login" : ROUTES.adminsLogin;
  if (role === "provider" || role === "staff") return portal === "provider" ? "/login" : ROUTES.providersLogin;
  return ROUTES.login;
}

export function getHomeRouteForRole(role?: string) {
  const portal = getPortalHost();
  if (role === "admin") return portal === "admin" ? "/dashboard" : ROUTES.adminsHome;
  if (role === "provider" || role === "staff") return portal === "provider" ? "/dashboard" : ROUTES.providersHome;
  return ROUTES.home;
}

export function getScopeFromRole(role?: string) {
  if (role === "admin") return "admins" as const;
  if (role === "provider" || role === "staff") return "providers" as const;
  return "user" as const;
}
